import { useState, useEffect } from 'react';
import api, { usersAPI, getErrorMessage } from '@/lib/api';
import { useLanguage } from '@/contexts/LanguageContext';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '@/components/ui/dialog';
import { formatDateTime } from '@/lib/utils';
import { 
  DollarSign,
  Plus,
  Edit,
  Trash2,
  Loader2,
  Calculator,
  Users,
  Clock
} from 'lucide-react';
import { toast } from 'sonner';

// Salary API functions
const salaryAPI = {
  list: () => api.get('/salaries'),
  getForEmployee: (employeeId, includeHistory = false) => 
    api.get(`/salaries/employee/${employeeId}`, { params: { include_history: includeHistory } }),
  create: (data) => api.post('/salaries', data),
  update: (id, data) => api.put(`/salaries/${id}`, data),
  delete: (id) => api.delete(`/salaries/${id}`),
  calculate: (employeeId, year, month) => 
    api.get('/salaries/calculate', { params: { employee_id: employeeId, year, month } })
};

const CURRENCIES = ['EUR', 'USD', 'RUB', 'GBP'];

export default function Salaries() {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [salaries, setSalaries] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [calcDialogOpen, setCalcDialogOpen] = useState(false);
  const [editingSalary, setEditingSalary] = useState(null);
  const [calculation, setCalculation] = useState(null);
  const [calculating, setCalculating] = useState(false);
  
  const SALARY_TYPES = {
    hourly: { label: t('salaries.hourly'), description: t('salaries.hourly') },
    daily: { label: t('salaries.daily'), description: t('salaries.daily') },
    monthly: { label: t('salaries.monthly'), description: t('salaries.monthly') }
  };

  const MONTHS = [
    t('months.january'), t('months.february'), t('months.march'), t('months.april'),
    t('months.may'), t('months.june'), t('months.july'), t('months.august'),
    t('months.september'), t('months.october'), t('months.november'), t('months.december')
  ];
  
  const [formData, setFormData] = useState({
    employee_id: '',
    salary_type: 'hourly',
    base_amount: '',
    currency: 'EUR',
    overtime_multiplier: 1.5,
    night_shift_bonus: 0,
    weekend_bonus: 0,
    notes: ''
  });
  
  const [calcForm, setCalcForm] = useState({
    employee_id: '',
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [salariesRes, employeesRes] = await Promise.all([
        salaryAPI.list(),
        usersAPI.list({ role: 'employee' })
      ]);
      setSalaries(salariesRes.data);
      setEmployees(employeesRes.data);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error(t('msg.failedLoad'));
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      employee_id: '',
      salary_type: 'hourly',
      base_amount: '',
      currency: 'EUR',
      overtime_multiplier: 1.5,
      night_shift_bonus: 0,
      weekend_bonus: 0,
      notes: ''
    });
    setEditingSalary(null);
  };

  const openCreateDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  const openEditDialog = (salary) => {
    setEditingSalary(salary);
    setFormData({
      employee_id: salary.employee_id,
      salary_type: salary.salary_type,
      base_amount: salary.base_amount.toString(),
      currency: salary.currency,
      overtime_multiplier: salary.overtime_multiplier,
      night_shift_bonus: salary.night_shift_bonus,
      weekend_bonus: salary.weekend_bonus,
      notes: salary.notes || ''
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const payload = {
      employee_id: formData.employee_id,
      salary_type: formData.salary_type,
      base_amount: parseFloat(formData.base_amount),
      currency: formData.currency,
      overtime_multiplier: parseFloat(formData.overtime_multiplier),
      night_shift_bonus: parseFloat(formData.night_shift_bonus),
      weekend_bonus: parseFloat(formData.weekend_bonus),
      notes: formData.notes || null
    };

    try {
      if (editingSalary) {
        await salaryAPI.update(editingSalary.id, payload);
        toast.success(t('salaries.configSaved'));
      } else {
        await salaryAPI.create(payload);
        toast.success(t('salaries.configSaved'));
      }
      setDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      console.error('Failed to save salary:', error);
      toast.error(getErrorMessage(error, t('salaries.configError')));
    }
  };

  const handleDelete = async (salaryId) => {
    if (!window.confirm(t('common.confirmDelete'))) return;
    
    try {
      await salaryAPI.delete(salaryId);
      toast.success(t('msg.deleteSuccess'));
      loadData();
    } catch (error) {
      console.error('Failed to delete salary:', error);
      toast.error(t('msg.failedDelete'));
    }
  };

  const handleCalculate = async () => {
    if (!calcForm.employee_id) {
      toast.error(t('salaries.selectEmployee'));
      return;
    }
    
    try {
      setCalculating(true);
      const res = await salaryAPI.calculate(
        calcForm.employee_id,
        calcForm.year,
        calcForm.month
      );
      setCalculation(res.data);
    } catch (error) {
      console.error('Failed to calculate salary:', error);
      toast.error(getErrorMessage(error, t('salaries.calcError')));
    } finally {
      setCalculating(false);
    }
  };

  const formatCurrency = (amount, currency) => {
    return new Intl.NumberFormat('en-EU', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const getEmployeeWithoutSalary = () => {
    const employeesWithSalary = new Set(salaries.map(s => s.employee_id));
    return employees.filter(e => !employeesWithSalary.has(e.id));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="relative">
          <Loader2 className="h-10 w-10 animate-spin text-wp-light" />
          <div className="absolute inset-0 blur-xl bg-wp-medium/30 animate-pulse" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="salaries-page">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold text-white font-heading flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center">
              <DollarSign className="h-6 w-6 text-emerald-400" />
            </div>
            {t('salaries.title')}
          </h1>
          <p className="text-wp-light/40 mt-2">{t('salaries.subtitle')}</p>
        </div>

        <div className="flex gap-3">
          {/* Calculate Salary Button */}
          <Dialog open={calcDialogOpen} onOpenChange={setCalcDialogOpen}>
            <DialogTrigger asChild>
              <button 
                className="flex items-center gap-2 px-5 py-2.5 bg-wp-dark/30 hover:bg-wp-dark/50 text-white font-medium rounded-xl transition-all border border-wp-dark/50"
                data-testid="calculate-salary-button"
              >
                <Calculator className="h-4 w-4" />
                {t('salaries.calculate')}
              </button>
            </DialogTrigger>
            <DialogContent className="max-w-lg bg-wp-darker border-wp-dark/50">
              <DialogHeader>
                <DialogTitle className="text-white font-heading">{t('salaries.calculate')}</DialogTitle>
                <DialogDescription className="text-wp-light/50">
                  {t('salaries.selectPeriod')}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-wp-light/70">{t('common.employee')}</Label>
                  <Select 
                    value={calcForm.employee_id} 
                    onValueChange={(v) => setCalcForm({...calcForm, employee_id: v})}
                  >
                    <SelectTrigger className="bg-wp-dark/30 border-wp-dark/50 text-white">
                      <SelectValue placeholder={t('salaries.selectEmployee')} />
                    </SelectTrigger>
                    <SelectContent className="bg-wp-darker border-wp-dark/50">
                      {employees.map(emp => (
                        <SelectItem key={emp.id} value={emp.id}>
                          {emp.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-wp-light/70">{t('reports.year')}</Label>
                    <Input
                      type="number"
                      value={calcForm.year}
                      onChange={(e) => setCalcForm({...calcForm, year: parseInt(e.target.value)})}
                      className="bg-wp-dark/30 border-wp-dark/50 text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-wp-light/70">{t('reports.month')}</Label>
                    <Select 
                      value={calcForm.month.toString()} 
                      onValueChange={(v) => setCalcForm({...calcForm, month: parseInt(v)})}
                    >
                      <SelectTrigger className="bg-wp-dark/30 border-wp-dark/50 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-wp-darker border-wp-dark/50">
                        {MONTHS.map((month, idx) => (
                          <SelectItem key={idx + 1} value={(idx + 1).toString()}>
                            {month}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <button
                  onClick={handleCalculate}
                  disabled={calculating || !calcForm.employee_id}
                  className="w-full flex items-center justify-center gap-2 px-5 py-2.5 bg-wp-medium hover:bg-wp-light text-white font-medium rounded-xl transition-all disabled:opacity-50"
                >
                  {calculating ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Calculator className="h-4 w-4" />
                  )}
                  {t('salaries.calculate')}
                </button>
                
                {/* Calculation Result */}
                {calculation && (
                  <div className="mt-4 p-4 rounded-xl bg-wp-dark/30 border border-wp-dark/50 space-y-3">
                    <h4 className="font-semibold text-white">{calculation.employee_name}</h4>
                    <p className="text-sm text-wp-light/50">
                      {t('salaries.period')}: {calculation.period_start} — {calculation.period_end}
                    </p>
                    
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div className="flex items-center gap-2 text-wp-light/70">
                        <Clock className="h-4 w-4" />
                        <span>{t('salaries.regularHours')}:</span>
                      </div>
                      <div className="text-white font-medium">{calculation.regular_hours.toFixed(1)}</div>
                      
                      {calculation.overtime_hours > 0 && (
                        <>
                          <div className="text-wp-light/70">{t('salaries.overtimeHours')}:</div>
                          <div className="text-white font-medium">{calculation.overtime_hours.toFixed(1)}</div>
                        </>
                      )}
                      
                      <div className="text-wp-light/70">{t('salaries.basePay')}:</div>
                      <div className="text-white font-medium">
                        {formatCurrency(calculation.base_pay, calculation.currency)}
                      </div>
                      
                      {calculation.overtime_pay > 0 && (
                        <>
                          <div className="text-wp-light/70">{t('salaries.overtimePay')}:</div>
                          <div className="text-white font-medium">
                            {formatCurrency(calculation.overtime_pay, calculation.currency)}
                          </div>
                        </>
                      )}
                    </div>
                    
                    <div className="pt-3 border-t border-wp-dark/50">
                      <div className="flex items-center justify-between">
                        <span className="text-white font-semibold">{t('salaries.totalGross')}:</span>
                        <span className="text-xl font-bold text-emerald-400">
                          {formatCurrency(calculation.total_gross, calculation.currency)}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>

          {/* Add Salary Button */}
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <button 
                onClick={openCreateDialog}
                className="flex items-center gap-2 px-5 py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white font-medium rounded-xl transition-all"
                data-testid="add-salary-button"
              >
                <Plus className="h-4 w-4" />
                {t('salaries.setupSalary')}
              </button>
            </DialogTrigger>
            <DialogContent className="max-w-lg bg-wp-darker border-wp-dark/50">
              <DialogHeader>
                <DialogTitle className="text-white font-heading">
                  {editingSalary ? t('common.edit') : t('salaries.setupSalary')}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Employee Selection */}
                <div className="space-y-2">
                  <Label className="text-wp-light/70">{t('common.employee')}</Label>
                  <Select 
                    value={formData.employee_id} 
                    onValueChange={(v) => setFormData({...formData, employee_id: v})}
                    disabled={!!editingSalary}
                  >
                    <SelectTrigger className="bg-wp-dark/30 border-wp-dark/50 text-white">
                      <SelectValue placeholder={t('salaries.selectEmployee')} />
                    </SelectTrigger>
                    <SelectContent className="bg-wp-darker border-wp-dark/50">
                      {(editingSalary ? employees : getEmployeeWithoutSalary()).map(emp => (
                        <SelectItem key={emp.id} value={emp.id}>
                          {emp.name} ({emp.email})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Salary Type */}
                <div className="space-y-2">
                  <Label className="text-wp-light/70">{t('salaries.salaryType')}</Label>
                  <Select 
                    value={formData.salary_type} 
                    onValueChange={(v) => setFormData({...formData, salary_type: v})}
                  >
                    <SelectTrigger className="bg-wp-dark/30 border-wp-dark/50 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-wp-darker border-wp-dark/50">
                      {Object.entries(SALARY_TYPES).map(([key, val]) => (
                        <SelectItem key={key} value={key}>
                          {val.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Base Amount and Currency */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="col-span-2 space-y-2">
                    <Label className="text-wp-light/70">{t('salaries.baseAmount')}</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.base_amount}
                      onChange={(e) => setFormData({...formData, base_amount: e.target.value})}
                      placeholder="15.00"
                      required
                      className="bg-wp-dark/30 border-wp-dark/50 text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-wp-light/70">{t('salaries.currency')}</Label>
                    <Select 
                      value={formData.currency} 
                      onValueChange={(v) => setFormData({...formData, currency: v})}
                    >
                      <SelectTrigger className="bg-wp-dark/30 border-wp-dark/50 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-wp-darker border-wp-dark/50">
                        {CURRENCIES.map(cur => (
                          <SelectItem key={cur} value={cur}>{cur}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Bonuses */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label className="text-wp-light/70">{t('salaries.overtimeMultiplier')}</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={formData.overtime_multiplier}
                      onChange={(e) => setFormData({...formData, overtime_multiplier: e.target.value})}
                      className="bg-wp-dark/30 border-wp-dark/50 text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-wp-light/70">{t('salaries.nightBonus')}</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.night_shift_bonus}
                      onChange={(e) => setFormData({...formData, night_shift_bonus: e.target.value})}
                      className="bg-wp-dark/30 border-wp-dark/50 text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-wp-light/70">{t('salaries.weekendBonus')}</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.weekend_bonus}
                      onChange={(e) => setFormData({...formData, weekend_bonus: e.target.value})}
                      className="bg-wp-dark/30 border-wp-dark/50 text-white"
                    />
                  </div>
                </div>

                {/* Notes */}
                <div className="space-y-2">
                  <Label className="text-wp-light/70">{t('common.description')}</Label>
                  <Input
                    value={formData.notes}
                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                    className="bg-wp-dark/30 border-wp-dark/50 text-white"
                  />
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <button 
                    type="button" 
                    onClick={() => setDialogOpen(false)}
                    className="px-5 py-2.5 bg-wp-dark/30 hover:bg-wp-dark/50 text-white rounded-xl border border-wp-dark/50 transition-colors"
                  >
                    {t('common.cancel')}
                  </button>
                  <button 
                    type="submit" 
                    className="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white font-medium rounded-xl transition-all"
                  >
                    {editingSalary ? t('common.update') : t('common.save')}
                  </button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        <div className="glass-card rounded-2xl p-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center">
              <Users className="h-5 w-5 text-emerald-400" />
            </div>
            <div>
              <p className="text-wp-light/40 text-sm">{t('common.configured')}</p>
              <p className="text-2xl font-bold text-white">{salaries.length}</p>
            </div>
          </div>
        </div>
        <div className="glass-card rounded-2xl p-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center">
              <Users className="h-5 w-5 text-amber-400" />
            </div>
            <div>
              <p className="text-wp-light/40 text-sm">{t('salaries.notConfigured')}</p>
              <p className="text-2xl font-bold text-white">{getEmployeeWithoutSalary().length}</p>
            </div>
          </div>
        </div>
        <div className="glass-card rounded-2xl p-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-wp-medium/20 flex items-center justify-center">
              <Clock className="h-5 w-5 text-wp-light" />
            </div>
            <div>
              <p className="text-wp-light/40 text-sm">{t('salaries.hourly')}</p>
              <p className="text-2xl font-bold text-white">
                {salaries.filter(s => s.salary_type === 'hourly').length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Salaries List */}
      {salaries.length === 0 ? (
        <div className="glass-card rounded-2xl p-12 text-center">
          <DollarSign className="h-12 w-12 mx-auto text-white/20 mb-4" />
          <h3 className="text-lg font-medium text-white">{t('common.noData')}</h3>
          <p className="text-wp-light/40 mt-1">{t('salaries.subtitle')}</p>
        </div>
      ) : (
        <div className="glass-card rounded-2xl overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-wp-dark/50">
                <th className="text-left py-4 px-6 text-wp-light/50 font-medium">{t('common.employee')}</th>
                <th className="text-left py-4 px-6 text-wp-light/50 font-medium">{t('common.type')}</th>
                <th className="text-left py-4 px-6 text-wp-light/50 font-medium">{t('salaries.baseAmount')}</th>
                <th className="text-left py-4 px-6 text-wp-light/50 font-medium">{t('salaries.overtimeMultiplier')}</th>
                <th className="text-left py-4 px-6 text-wp-light/50 font-medium">{t('common.date')}</th>
                <th className="text-right py-4 px-6 text-wp-light/50 font-medium">{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {salaries.map((salary) => (
                <tr 
                  key={salary.id} 
                  className="border-b border-wp-dark/30 hover:bg-wp-dark/30 transition-colors"
                  data-testid={`salary-row-${salary.id}`}
                >
                  <td className="py-4 px-6">
                    <span className="text-white font-medium">{salary.employee_name}</span>
                  </td>
                  <td className="py-4 px-6">
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      salary.salary_type === 'hourly' ? 'bg-wp-medium/20 text-wp-light' :
                      salary.salary_type === 'daily' ? 'bg-amber-500/20 text-amber-400' :
                      'bg-emerald-500/20 text-emerald-400'
                    }`}>
                      {SALARY_TYPES[salary.salary_type]?.label}
                    </span>
                  </td>
                  <td className="py-4 px-6 text-white">
                    {formatCurrency(salary.base_amount, salary.currency)}
                  </td>
                  <td className="py-4 px-6 text-wp-light/70">
                    x{salary.overtime_multiplier}
                  </td>
                  <td className="py-4 px-6 text-wp-light/50 text-sm">
                    {formatDateTime(salary.effective_from)}
                  </td>
                  <td className="py-4 px-6">
                    <div className="flex items-center justify-end gap-2">
                      <button 
                        onClick={() => openEditDialog(salary)}
                        className="p-2 hover:bg-wp-dark/50 rounded-lg text-wp-light/50 hover:text-wp-lightest transition-colors"
                        title={t('common.edit')}
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button 
                        onClick={() => handleDelete(salary.id)}
                        className="p-2 hover:bg-rose-500/20 rounded-lg text-wp-light/50 hover:text-rose-400 transition-colors"
                        title={t('common.delete')}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
